import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    PDFViewer,
    PDFDownloadLink
} from '@react-pdf/renderer';
import { Download, Clock } from 'lucide-react';

// ⬅ Ajuste ce chemin selon l'emplacement réel du logo dans ton projet
//import logoSoplast from '../../logo-soplast.png';
import logoSoplast from '../../logoSoplast';


// Informations légales de l'émetteur (identiques au bon de livraison)
const COMPANY = {
    addressLine: 'Point E, Rue G X, Rue de Kolda',
    city: 'Dakar-Senegal',
    phone: '+221 33 805 47 50 / 78 911 95 27',
    ninea: '012093530',
    rc: 'SN DKR 2025 B 16191',
    bank: 'Banque BNDE: SN169 00106309301 54',
};



// ===== Conversion d'un nombre en lettres (français) =====
const numberToWords = (num) => {
    if (num === 0) return 'zéro';

    const ones = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];

    const convertHundreds = (n) => {
        let result = '';
        const hundreds = Math.floor(n / 100);
        const remainder = n % 100;

        if (hundreds > 0) {
            result += hundreds === 1 ? 'cent' : ones[hundreds] + ' cent';
            if (remainder === 0 && hundreds > 1) result += 's';
        }

        if (remainder > 0) {
            if (result) result += ' ';
            if (remainder < 10) {
                result += ones[remainder];
            } else if (remainder < 20) {
                result += teens[remainder - 10];
            } else {
                const tensDigit = Math.floor(remainder / 10);
                const onesDigit = remainder % 10;
                result += tens[tensDigit];
                if (onesDigit > 0) {
                    if (tensDigit === 2 || tensDigit === 3 || tensDigit === 4 || tensDigit === 5 || tensDigit === 6) {
                        result += '-' + ones[onesDigit];
                    } else if (tensDigit === 7 || tensDigit === 9) {
                        result += '-' + teens[onesDigit];
                    } else if (tensDigit === 8) {
                        result += onesDigit === 1 ? '-un' : '-' + ones[onesDigit];
                    }
                }
            }
        }
        return result;
    };

    let integerPart = Math.floor(num);
    let result = '';

    if (integerPart >= 1000000) {
        const millions = Math.floor(integerPart / 1000000);
        result += convertHundreds(millions) + (millions === 1 ? ' million' : ' millions');
        integerPart = integerPart % 1000000;
    }
    if (integerPart >= 1000) {
        const thousands = Math.floor(integerPart / 1000);
        if (result) result += ' ';
        result += thousands === 1 ? 'mille' : convertHundreds(thousands) + ' mille';
        integerPart = integerPart % 1000;
    }
    if (integerPart > 0) {
        if (result) result += ' ';
        result += convertHundreds(integerPart);
    }

    result += ' francs CFA';
    return result;
};

// Format de date jj/mm/aaaa
const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
};

// Format monétaire avec séparateur d'espace
const formatCurrency = (amount) => {
    const rounded = Math.round(Number(amount) || 0);
    return `${rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} FCFA`;
};

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        paddingTop: 30,
        paddingLeft: 35,
        paddingRight: 35,
        paddingBottom: 40,
        backgroundColor: '#ffffff',
        color: '#000000',
    },

    // ===== En-tête =====
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 10,
        borderBottomWidth: 1.5,
        borderBottomColor: '#000000',
        borderBottomStyle: 'solid',
    },
    headerLeft: { flexDirection: 'row', alignItems: 'center' },
    logo: { width: 60, height: 38, marginRight: 8 },
    companyName: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#000000', lineHeight: 1.05 },
    headerRight: { textAlign: 'left' },
    headerInfo: { fontSize: 8.5, color: '#000000', marginBottom: 1.5 },

    // ===== Bloc client / titre =====
    midSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 14,
        marginBottom: 12,
    },
    clientBlock: { width: '55%' },
    clientLabel: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
    clientName: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
    clientLine: { fontSize: 9, marginBottom: 1 },
    orderNumberLine: {
        marginTop:2,
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 1,
    },
    titleBlock: { width: '40%', alignItems: 'flex-end' },
    docTitle: { fontSize: 18, fontFamily: 'Helvetica-Bold', marginBottom: 6 },
    docMetaRow: { flexDirection: 'row', justifyContent: 'flex-end' },
    docMetaLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginRight: 8 },
    docMetaValue: { fontSize: 9, minWidth: 60, textAlign: 'left' },

    // ===== Tableau produits =====
    table: {
        borderWidth: 1,
        borderColor: '#000000',
        borderStyle: 'solid',
        marginBottom: 14,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#666666',
        borderBottomStyle: 'solid',
        alignItems: 'stretch',
        minHeight: 22,
    },
    tableHeaderRow: {
        backgroundColor: '#dce6f1',
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
    },
    cell: {
        paddingVertical: 4,
        paddingHorizontal: 4,
        borderRightWidth: 0.5,
        borderRightColor: '#666666',
        borderRightStyle: 'solid',
        justifyContent: 'center',
    },
    cellLast: { borderRightWidth: 0 },
    headerCellText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },
    cellText: { fontSize: 8.5, textAlign: 'left' },
    cellTextCenter: { fontSize: 8.5, textAlign: 'center' },
    cellTextRight: { fontSize: 8.5, textAlign: 'right' },

    // Largeurs colonnes (somme = 100%)
    colRef: { width: '9%' },
    colEan: { width: '18%' },
    colLib: { width: '33%' },
    colQte: { width: '10%' },
    colPU: { width: '15%' },
    colTotal: { width: '15%' },

    // ===== Totaux =====
    totalsSection: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 12,
    },
    totalsBox: {
        width: '45%',
        borderWidth: 1,
        borderColor: '#000000',
        borderStyle: 'solid',
    },
    totalsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderBottomWidth: 0.5,
        borderBottomColor: '#666666',
    },
    totalsRowFinal: {
        backgroundColor: '#dce6f1',
        borderBottomWidth: 0,
    },
    totalsLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
    totalsValue: { fontSize: 9, textAlign: 'right' },
    totalsLabelFinal: { fontSize: 10, fontFamily: 'Helvetica-Bold' },
    totalsValueFinal: { fontSize: 10, fontFamily: 'Helvetica-Bold', textAlign: 'right' },

    // ===== Montant en lettres =====
    amountInWords: {
        borderWidth: 1,
        borderColor: '#000000',
        borderStyle: 'solid',
        padding: 8,
        marginBottom: 14,
    },
    amountInWordsText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

    notes: { marginTop: 4 },
    notesTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
    notesText: { fontSize: 9 },
});

// Composant contenu du PDF
const QuotePDF = ({ order }) => {
    const isQuote = order.status === 'PENDING';
    const documentTitle = isQuote ? 'DEVIS' : 'BON DE COMMANDE';
    const documentTypeLower = isQuote ? 'devis' : 'commande';

    const items = Array.isArray(order.items) ? order.items : [];

    // Calculs financiers
    const totalHT = items.reduce(
        (acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 0)),
        0
    );
    const isTVAApplicable = order.partner && order.partner.vatApplicable === true;
    const tva = isTVAApplicable ? totalHT * 0.18 : 0;
    const totalTTC = totalHT + tva;
    const finalLabel = isTVAApplicable ? 'Total TTC' : 'Net à payer';
    const finalAmount = isTVAApplicable ? totalTTC : totalHT;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* En-tête : logo + bloc légal */}
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Image src={logoSoplast} style={styles.logo} />
                        <Text style={styles.companyName}>SENEGAL{'\n'}SOPLAST</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.headerInfo}>{COMPANY.addressLine}</Text>
                        <Text style={styles.headerInfo}>{COMPANY.city}</Text>
                        <Text style={styles.headerInfo}>Téléphone: {COMPANY.phone}</Text>
                        <Text style={styles.headerInfo}>NINEA: {COMPANY.ninea}</Text>
                        <Text style={styles.headerInfo}>RC: {COMPANY.rc}</Text>
                        <Text style={styles.headerInfo}>{COMPANY.bank}</Text>
                    </View>
                </View>

                {/* Bloc client (gauche) + titre document (droite) */}
                <View style={styles.midSection}>
                    <View style={styles.clientBlock}>
                        <Text style={styles.clientLabel}>CLIENT</Text>
                        <Text style={styles.clientName}>{order.partner?.name}</Text>
                        {order.partner?.address && (
                            <Text style={styles.clientLine}>Adresse: {order.partner.address}</Text>
                        )}
                        {order.partner?.phone && (
                            <Text style={styles.clientLine}>Tél: {order.partner.phone}</Text>
                        )}
                        {order.orderNumber && (
                            <Text style={styles.orderNumberLine}>Référence commande: {order.referenceClient}</Text>
                        )}
                    </View>

                    <View style={styles.titleBlock}>
                        <Text style={styles.docTitle}>{documentTitle}</Text>
                        <View style={styles.docMetaRow}>
                            <Text style={styles.docMetaLabel}>N°:</Text>
                            <Text style={styles.docMetaValue}>{order.orderNumber}</Text>
                        </View>
                        <View style={styles.docMetaRow}>
                            <Text style={styles.docMetaLabel}>Date:</Text>
                            <Text style={styles.docMetaValue}>{formatDate(order.orderDate)}</Text>
                        </View>
                    </View>
                </View>

                {/* Tableau produits */}
                <View style={styles.table}>
                    {/* En-tête */}
                    <View style={[styles.tableRow, styles.tableHeaderRow]}>
                        <View style={[styles.cell, styles.colRef]}>
                            <Text style={styles.headerCellText}>Ref</Text>
                        </View>
                        <View style={[styles.cell, styles.colEan]}>
                            <Text style={styles.headerCellText}>EAN principal</Text>
                        </View>
                        <View style={[styles.cell, styles.colLib]}>
                            <Text style={styles.headerCellText}>Libellé</Text>
                        </View>
                        <View style={[styles.cell, styles.colQte]}>
                            <Text style={styles.headerCellText}>Quantité</Text>
                        </View>
                        <View style={[styles.cell, styles.colPU]}>
                            <Text style={styles.headerCellText}>Prix Unitaire</Text>
                        </View>
                        <View style={[styles.cell, styles.cellLast, styles.colTotal]}>
                            <Text style={styles.headerCellText}>Total</Text>
                        </View>
                    </View>

                    {/* Lignes */}
                    {items.length > 0 ? (
                        items.map((item, index) => (
                            <View key={index} style={styles.tableRow}>
                                <View style={[styles.cell, styles.colRef]}>
                                    <Text style={styles.cellTextCenter}>{item.product?.numeroProduit}</Text>
                                </View>
                                <View style={[styles.cell, styles.colEan]}>
                                    <Text style={styles.cellTextCenter}>{item.product?.codeBarre}</Text>
                                </View>
                                <View style={[styles.cell, styles.colLib]}>
                                    <Text style={styles.cellText}>{item.product?.name+'-'+item.product?.sku}</Text>
                                </View>
                                <View style={[styles.cell, styles.colQte]}>
                                    <Text style={styles.cellTextCenter}>{item.quantity}</Text>
                                </View>
                                <View style={[styles.cell, styles.colPU]}>
                                    <Text style={styles.cellTextRight}>{formatCurrency(item.unitPrice)}</Text>
                                </View>
                                <View style={[styles.cell, styles.cellLast, styles.colTotal]}>
                                    <Text style={styles.cellTextRight}>{formatCurrency((Number(item.unitPrice) || 0) * (Number(item.quantity) || 0))}</Text>
                                </View>
                            </View>
                        ))
                    ) : (
                        <View style={styles.tableRow}>
                            <View style={{ width: '100%', padding: 6 }}>
                                <Text style={styles.cellText}>Aucun produit dans ce {documentTypeLower}</Text>
                            </View>
                        </View>
                    )}
                </View>

                {/* Totaux */}
                <View style={styles.totalsSection}>
                    <View style={styles.totalsBox}>
                        {isTVAApplicable && (
                            <>
                                <View style={styles.totalsRow}>
                                    <Text style={styles.totalsLabel}>Total HT:</Text>
                                    <Text style={styles.totalsValue}>{formatCurrency(totalHT)}</Text>
                                </View>
                                <View style={styles.totalsRow}>
                                    <Text style={styles.totalsLabel}>TVA 18%:</Text>
                                    <Text style={styles.totalsValue}>{formatCurrency(tva)}</Text>
                                </View>
                            </>
                        )}
                        <View style={[styles.totalsRow, styles.totalsRowFinal]}>
                            <Text style={styles.totalsLabelFinal}>{finalLabel}:</Text>
                            <Text style={styles.totalsValueFinal}>{formatCurrency(finalAmount)}</Text>
                        </View>
                    </View>
                </View>

                {/* Montant en lettres */}
                <View style={styles.amountInWords}>
                    <Text style={styles.amountInWordsText}>
                        Arrêté le présent {documentTypeLower} à la somme de : {numberToWords(finalAmount)}
                    </Text>
                </View>

                {/* Notes si disponibles */}
                {order.notes && (
                    <View style={styles.notes}>
                        <Text style={styles.notesTitle}>Notes:</Text>
                        <Text style={styles.notesText}>{order.notes}</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

// Aperçu
const QuotePDFViewer = ({ order, company }) => (
    <PDFViewer width="100%" height={600} style={{ border: 'none' }}>
        <QuotePDF order={order} company={company} />
    </PDFViewer>
);

// Lien de téléchargement
const QuotePDFDownloadLink = ({ order, company }) => {
    const isQuote = order.status === 'PENDING';
    const documentType = isQuote ? 'devis' : 'commande';
    return (
        <PDFDownloadLink
            document={<QuotePDF order={order} company={company} />}
            fileName={`${documentType}_${order.orderNumber}.pdf`}
            style={{
                textDecoration: 'none',
                padding: '0.25rem 0.5rem',
                backgroundColor: 'transparent',
                color: '#2563eb',
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                minHeight: '31px',
            }}
            className="me-1"
        >
            {({ blob, url, loading, error }) =>
                loading ? <Clock size={16} /> : <Download size={16} />
            }
        </PDFDownloadLink>
    );
};

// Composant principal
const GenererPdfDevis = ({ order, company, showPreview = false, onDownload = null }) => {
    const isQuote = order.status === 'PENDING';
    const documentType = isQuote ? 'devis' : 'commande';

    if (onDownload) {
        return (
            <PDFDownloadLink
                document={<QuotePDF order={order} company={company} />}
                fileName={`${documentType}_${order.orderNumber}.pdf`}
                style={{ display: 'none' }}
            >
                {({ blob, url, loading, error }) => {
                    if (!loading && !error && blob) {
                        onDownload(blob);
                    } else if (error) {
                        console.error("Erreur lors de la génération du PDF:", error);
                    }
                    return null;
                }}
            </PDFDownloadLink>
        );
    }

    return (
        <div>
            {showPreview ? (
                <QuotePDFViewer order={order} company={company} />
            ) : (
                <QuotePDFDownloadLink order={order} company={company} />
            )}
        </div>
    );
};

export default GenererPdfDevis;